// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. The original lives
// in @roc/feature-borrowers and is left untouched so other portals keep the old
// design. MobX stores/services remain shared (imported from @roc/feature-borrowers).

import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { createRadioField } from '@roc/ui';
import { yesNoOptions } from '@roc/feature-utils';
import { CompactFileUploadAreaWithDownload } from '../components/compactFileUploadAreaWithDownload';
import { useState } from 'react';

// Minimal shape for a license upload row (avoids a deep @roc/ui import).
type LicenseUpload = {
  name?: string;
  file?: any;
  type?: string;
  taskId?: any;
  originalFileName?: string;
  fileExtension?: string;
  disabled?: boolean;
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    questionBox: {
      padding: '4px 0',
    },
    blueRadio: {
      '& .MuiRadio-colorPrimary.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
  };
});

export const TrackRecordThirdStep = observer(({ store, documentStore }: { store: any; documentStore?: any }) => {
  const formStore = store.trackRecordOtherFormStore;
  const classes = useStyles();
  const [generalContractorUploads, setGeneralContractorUploads] = useState<LicenseUpload[]>([]);
  const [brokerUploads, setBrokerUploads] = useState<LicenseUpload[]>([]);

  const findFreshUpload = (items: LicenseUpload[]) =>
    items.find((it) => it?.file != null && !it.taskId);

  // Read-only row that replaces the optimistic one after a successful upload.
  const toPersistedRow = (taskId: any, fresh: LicenseUpload): LicenseUpload => ({
    taskId,
    disabled: true,
    name: fresh.name,
    fileExtension: (fresh.name || '').split('.').pop(),
    file: { path: fresh.name, name: fresh.name },
  });

  // Single license file: show the dropped file, upload it (reusing the existing task or replace)
  const handleLicenseChange = async (
    sectionProcess: string,
    current: LicenseUpload[],
    resolvedTaskId,
    updateFunc,
    setStoreItems: (items: LicenseUpload[]) => void,
    setLocalItems: (items: LicenseUpload[]) => void,
  ) => {
    const updated = updateFunc(current || []);
    const fresh = findFreshUpload(updated);
    if (!fresh) {
      setStoreItems(updated);
      setLocalItems(updated);
      return;
    }
    const previous = current || [];
    const existingTaskId = resolvedTaskId ?? previous.find((it) => it?.taskId)?.taskId;
    setStoreItems([fresh]);
    setLocalItems([fresh]);
    store.setUploadingLicense(true);
    try {
      const res = await store.uploadLicenseFile(sectionProcess, fresh, existingTaskId, documentStore);
      if (!res?.taskId) {
        setStoreItems(previous);
        setLocalItems(previous);
        return;
      }
      const persisted = toPersistedRow(res.taskId, fresh);
      setStoreItems([persisted]);
      setLocalItems([persisted]);
    } catch (e) {
      setStoreItems(previous);
      setLocalItems(previous);
      store.globalStore?.notificationStore?.showErrorNotification?.({ message: 'Error while uploading the license' });
    } finally {
      store.setUploadingLicense(false);
    }
  };

  const handleGeneralContractorUploadsChange = (updateFunc) =>
    handleLicenseChange(
      'BORROWER_GC_LICENSE',
      store.generalContractorUploads,
      store.gcLicenseTaskId,
      updateFunc,
      (items) => store.setGeneralContractorUploads(items),
      setGeneralContractorUploads,
    );

  const handleBrokerUploadsChange = (updateFunc) =>
    handleLicenseChange(
      'BORROWER_BROKER_LICENSE',
      store.brokerUploads,
      store.brokerLicenseTaskId,
      updateFunc,
      (items) => store.setBrokerUploads(items),
      setBrokerUploads,
    );

  // Download via documentStore.downloadDocument (downloadByTaskId); fallback to the store.
  const downloadLicense = (taskId: any, name: string, fileExtension?: string) =>
    documentStore?.downloadDocument
      ? documentStore.downloadDocument({ taskId, originalFileName: name, fileExtension, taskName: name })
      : store.downloadTaskFile(taskId, name, fileExtension);

  return (
    <Grid container direction='row' spacing={3} style={{ padding: '16px 0 8px' }}>
      {/* ── Top row: General Contractor + Broker, each with inline upload when Yes ── */}
      <Grid item xs={12} sm={6}>
        <Box className={`${classes.questionBox} ${classes.blueRadio}`}>
          {createRadioField({
            store: formStore,
            testId: 'generalContractor',
            fieldName: 'generalContractor',
            label: store?.isTpoFlow ? "Is the borrower a General Contractor?" : "Are you a General Contractor?",
            options: yesNoOptions,
            row: true,
          })}
        </Box>
        {formStore.form.fields.generalContractor.value === 'Yes' && (
          <CompactFileUploadAreaWithDownload
            items={store.generalContractorUploads}
            setItems={handleGeneralContractorUploadsChange}
            multiple={true}
            store={store}
            documentLabel="your General Contractor license"
            downloadHandler={downloadLicense}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box className={`${classes.questionBox} ${classes.blueRadio}`}>
          {createRadioField({
            store: formStore,
            testId: 'broker',
            fieldName: 'broker',
            label: store?.isTpoFlow ? "Is the borrower a Real Estate Broker / Agent?" : "Are you a Real Estate Broker / Agent?",
            options: yesNoOptions,
            row: true,
          })}
        </Box>
        {formStore.form.fields.broker.value === 'Yes' && (
          <CompactFileUploadAreaWithDownload
            items={store.brokerUploads}
            setItems={handleBrokerUploadsChange}
            multiple={true}
            store={store}
            documentLabel="your Real Estate Broker / Agent license"
            downloadHandler={downloadLicense}
          />
        )}
      </Grid>

      {/* ── Divider between top questions and experience question ── */}
      <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
        <Divider />
      </Grid>

      {/* ── Real estate experience question ── */}
      <Grid item xs={12}>
        <Box className={`${classes.questionBox} ${classes.blueRadio}`}>
          {createRadioField({
            store: formStore,
            testId: 'previousExperience',
            fieldName: 'previousExperience',
            label: "Do you have any Real Estate Experience?",
            options: yesNoOptions,
            row: true,
          })}
        </Box>
      </Grid>
    </Grid>
  );
});
